import { useRef } from "react";

const MIN = 0.06;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const CORNERS = { nw: "-left-1.5 -top-1.5 cursor-nwse-resize", ne: "-right-1.5 -top-1.5 cursor-nesw-resize", sw: "-left-1.5 -bottom-1.5 cursor-nesw-resize", se: "-right-1.5 -bottom-1.5 cursor-nwse-resize" };

// Resize from corner c, anchored at the opposite corner. ratio (h/w in stage units) keeps it square on screen.
function resize(b, c, dx, dy, ratio) {
  const left = c.includes("w"), top = c.includes("n");
  const ax = left ? b.x + b.w : b.x, ay = top ? b.y + b.h : b.y;
  let w = Math.min(Math.max(MIN, b.w + (left ? -dx : dx)), left ? ax : 1 - ax);
  let h = Math.min(Math.max(MIN, b.h + (top ? -dy : dy)), top ? ay : 1 - ay);
  if (ratio) { w = Math.min(w, h / ratio, (top ? ay : 1 - ay) / ratio); h = w * ratio; }
  return { x: left ? ax - w : ax, y: top ? ay - h : ay, w, h };
}

// Draggable box with 4 resize handles, positioned in 0-1 stage coordinates.
export function DragBox({ stageRef, box, onChange, onCommit, ratio, testId, label, onSelect, active }) {
  const start = useRef(null);
  const down = (mode) => (e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    onSelect?.();
    start.current = { mode, px: e.clientX, py: e.clientY, box };
  };
  const move = (e) => {
    const s = start.current;
    if (!s) return;
    const st = stageRef.current.getBoundingClientRect();
    const dx = (e.clientX - s.px) / st.width, dy = (e.clientY - s.py) / st.height, b = s.box;
    onChange(s.mode === "move"
      ? { ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) }
      : resize(b, s.mode, dx, dy, ratio && ratio()));
  };
  const up = () => { if (start.current) { start.current = null; onCommit(); } };
  const handlers = { onPointerMove: move, onPointerUp: up, onPointerCancel: up };

  return (
    <div data-testid={testId} onPointerDown={down("move")} {...handlers} title={`Drag to move ${label}`}
      className={`absolute border-2 border-dashed ${active ? "border-yellow-400" : "border-white/80"} hover:border-yellow-400 cursor-move touch-none select-none transition-colors`}
      style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }}>
      {Object.entries(CORNERS).map(([c, cls]) => (
        <span key={c} data-testid={`${testId}-handle-${c}`} onPointerDown={down(c)} {...handlers}
          className={`absolute z-20 w-3 h-3 bg-white border border-zinc-900 rounded-[2px] ${cls}`} />
      ))}
    </div>
  );
}
